import React, { useRef, useState, useEffect } from 'react'
import { FaPlay } from 'react-icons/fa'
import { ChevronRight, ChevronLeft } from 'react-bootstrap-icons'
import { cardsData } from './CarouselCards'

function MobileCard({ card, isActive, cardContentRef, activeSubCardIndex, onNext, onPrev, hasNext, hasPrev, videoLink, transitionDirection }) {
  const activeSubCard = card.subCards[activeSubCardIndex]
  const [isPlaying, setIsPlaying] = useState(false)
  const videoRef = useRef(null)

  useEffect(() => {
    // When sub-card or video changes, auto-play from start if there is a video
    if (videoRef.current && videoLink && activeSubCardIndex > 0) {
      const playFromStart = async () => {
        try {
          videoRef.current.currentTime = 0
          await videoRef.current.play()
          setIsPlaying(true)
        } catch {
          // Autoplay might be blocked; user can tap play button
          setIsPlaying(false)
        }
      }
      playFromStart()
    } else if (videoRef.current) {
      // Reset when no video or back to first sub-card
      videoRef.current.pause()
      videoRef.current.currentTime = 0
      setIsPlaying(false)
    }
  }, [activeSubCardIndex, videoLink])

  const handlePlayClick = () => {
    if (!videoRef.current) return
    if (isPlaying) {
      videoRef.current.pause()
    } else {
      videoRef.current.play().catch(() => {})
    }
    setIsPlaying(!isPlaying)
  }
  
  return (
    <div 
      className="relative rounded-t-[25px] rounded-b-[55px] shadow-2xl pb-0 overflow-hidden h-[520px] w-full" 
      style={{ 
        background: card.bgColor,
        opacity: 1,
        mixBlendMode: 'normal',
        filter: 'none',
        willChange: 'auto'
      }}
    >
      {/* Top Header Bar */}
      <div className="flex justify-between items-center px-4 py-3">
        {/* Left: Step indicator */}
        <div className="flex items-center gap-2 w-full">
          <div className="flex items-center justify-between px-1 w-full py-1 pb-0 rounded-full">
            <div className="flex items-center gap-2">
              <span className="text-white text-xs border-2 border-white rounded-full px-2.5 py-1 font-medium" style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}>{card.step}</span>
              <span className="text-white text-base font-medium" style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}>{card.title}</span>    
            </div>
            <div className="flex items-center gap-3" />
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="relative h-[calc(100%-64px)] flex flex-col">

        {/* Sub-Card Content */}
        <div className="flex-1 relative overflow-hidden flex flex-col">
          <div ref={cardContentRef} className="flex-1 overflow-hidden">
            {/* First Sub-Card: Only Bullets */}
            {activeSubCardIndex === 0 ? (
              <div className="h-full flex flex-col justify-center px-4">
                <div className="space-y-4 w-full px-2">
                  {activeSubCard.bullets.map((bullet, idx) => {
                    const words = bullet.split(' ')
                    const firstWord = words[0]
                    const restOfText = words.slice(1).join(' ')
                    return (
                      <div key={idx} className="flex items-start gap-3">
                        <p
                          className="text-white mt-1 text-sm leading-relaxed font-medium"
                          style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
                        >
                          <span className="text-2xl uppercase">
                            {firstWord}
                          </span>
                          {restOfText && (
                            <span
                              className="text-xl leading-relaxed font-medium"
                              style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
                            >
                              {' '}
                              {restOfText}
                            </span>
                          )}
                        </p>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : (
              /* Other Sub-Cards: Text + keyword tags + video, with slide/stack animation */
              <div className="px-4 py-4 pt-0 space-y-3">
                <div
                  className={`space-y-4 pt-2 mobile-card-content ${
                    transitionDirection === 'next'
                      ? 'mobile-card-slide-left'
                      : transitionDirection === 'prev'
                      ? 'mobile-card-slide-right'
                      : ''
                  }`}
                >
                    {/* Description */}
                   {/* Video */}
                  <div className="relative bg-gray-900/60 backdrop-blur-sm rounded-xl overflow-hidden aspect-video flex items-center justify-center border border-white/10">
                    {videoLink ? (
                      <>
                        <video
                          ref={videoRef}
                          src={videoLink}
                          className="w-full h-full object-cover"
                          loop
                          muted
                          playsInline
                          preload="metadata"
                          onPlay={() => setIsPlaying(true)}
                          onPause={() => setIsPlaying(false)}
                          onLoadedMetadata={(e) => {
                            e.target.currentTime = 0.1
                          }}
                        />
                        {!isPlaying && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <button
                              onClick={handlePlayClick}
                              className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-white/30 transition-all group"
                            >
                              <FaPlay className="w-4 h-4 text-white ml-0.5 group-hover:scale-110 transition-transform" />
                            </button>
                          </div>
                        )}
                        {isPlaying && (
                          <button
                            onClick={handlePlayClick}
                            className="absolute inset-0 flex items-center justify-center bg-transparent hover:bg-black/20 transition-all cursor-pointer opacity-0 hover:opacity-100"
                          >
                            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
                              <div className="flex items-center justify-center gap-1">
                                <div className="w-1.5 h-6 bg-white rounded-sm"></div>
                                <div className="w-1.5 h-6 bg-white rounded-sm"></div>
                              </div>
                            </div>
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-white/30 transition-all group">
                            <FaPlay className="w-4 h-4 text-white ml-0.5 group-hover:scale-110 transition-transform" />
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="mt-1">
                    <p
                      className="text-white/90 text-sm leading-relaxed"
                      style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
                    >
                      {activeSubCard.content}
                    </p>
                    {/* Straight, compact keyword tags for mobile */}
                    {activeSubCard.keywords && activeSubCard.keywords.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {activeSubCard.keywords.map((keyword) => (
                          <span
                            key={keyword}
                            className="inline-flex px-2.5 py-1 rounded-full bg-white/95 text-gray-900 text-[11px] leading-snug font-medium shadow-sm shadow-black/5"
                            style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                 
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation arrows at bottom - shown on all pages */}
        <div className="flex items-center justify-between px-4 pb-3">
          <button
            aria-label="Previous slide"
            onClick={onPrev}
            disabled={!hasPrev}
            className={`flex items-center justify-center w-20 h-20 rounded-full text-white transition-all ${
              hasPrev ? 'opacity-100' : 'opacity-40 cursor-not-allowed'
            }`}
          >
            <ChevronLeft className="text-sm" style={{ strokeWidth: 2, stroke: 'currentColor', fill: 'none' }} />
          </button>
          <button
            aria-label="Next slide"
            onClick={onNext}
            disabled={!hasNext}
            className={`flex items-center justify-center w-20 h-20 rounded-full border border-white text-white transition-all ${
              hasNext ? 'opacity-100' : 'opacity-40 cursor-not-allowed'
            }`}
          >
            <ChevronRight className="text-sm" style={{ strokeWidth: 2, stroke: 'currentColor', fill: 'none' }} />
          </button>
        </div>
      </div>
    </div>
  )
}

function MobileCardCarousel({ card, videoData }) {
  const cardContentRef = useRef(null)
  const [activeSubCardIndex, setActiveSubCardIndex] = useState(0)
  const [transitionDirection, setTransitionDirection] = useState(null)

  // Touch handling for horizontal swipe between sub-cards
  const touchStartXRef = useRef(null)
  const touchEndXRef = useRef(null)

  const activeSubCard = card.subCards[activeSubCardIndex]

  // Resolve video URL using the explicit videoLink key on the sub-card
  const videoLink =
    activeSubCard &&
    videoData &&
    activeSubCard.videoLink
      ? videoData[activeSubCard.videoLink] || null
      : null

  const hasNext = activeSubCardIndex < card.subCards.length - 1
  const hasPrev = activeSubCardIndex > 0

  const goToNext = () => {
    if (hasNext) {
      setTransitionDirection('next')
      setActiveSubCardIndex(prev => prev + 1)
    }
  }

  const goToPrev = () => {
    if (hasPrev) {
      setTransitionDirection('prev')
      setActiveSubCardIndex(prev => prev - 1)
    }
  }

  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX
    touchEndXRef.current = null
  }

  const handleTouchMove = (e) => {
    touchEndXRef.current = e.touches[0].clientX
  }

  const handleTouchEnd = () => {
    if (touchStartXRef.current === null || touchEndXRef.current === null) return
    const deltaX = touchStartXRef.current - touchEndXRef.current
    const minSwipeDistance = 40

    if (Math.abs(deltaX) > minSwipeDistance) {
      if (deltaX > 0) {
        // swipe left -> next
        goToNext()
      } else {
        // swipe right -> previous
        goToPrev()
      }
    }

    touchStartXRef.current = null
    touchEndXRef.current = null
  }

  return (
    <div
      className="w-full"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <MobileCard
        card={card}
        isActive={true}
        cardContentRef={cardContentRef}
        activeSubCardIndex={activeSubCardIndex}
        onNext={goToNext}
        onPrev={goToPrev}
        hasNext={hasNext}
        hasPrev={hasPrev}
        videoLink={videoLink}
        transitionDirection={transitionDirection}
      />
    </div>
  )
}

export default function MobileCarouselCards() {
  // Hardcoded video data
  const videoData = {
    link1: 'https://d2s7q4z8t1mini.cloudfront.net/video/link1.mp4',
    link2: 'https://d2s7q4z8t1mini.cloudfront.net/video/link2.mp4',
    link3: 'https://d2s7q4z8t1mini.cloudfront.net/video/link3.mp4',
    link4: 'https://d2s7q4z8t1mini.cloudfront.net/video/link4.mp4',
    link5: 'https://d2s7q4z8t1mini.cloudfront.net/video/link5.mp4',
    link6: 'https://d2s7q4z8t1mini.cloudfront.net/video/link6.mp4',
    link7: 'https://d2s7q4z8t1mini.cloudfront.net/video/link7.mp4',
    link8: 'https://d2s7q4z8t1mini.cloudfront.net/video/link8.mp4',
    link9: 'https://d2s7q4z8t1mini.cloudfront.net/video/link9.mp4'
  }

  return (
    <div className="bg-white md:hidden py-8">
      <style>{`
        .mobile-card-content {
          transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s ease-out;
          will-change: transform, opacity;
        }
        .mobile-card-slide-left {
          animation: mobileCardSlideLeft 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .mobile-card-slide-right {
          animation: mobileCardSlideRight 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .thick-icon svg {
          stroke-width: 2.5;
        }
        .thick-icon svg path {
          stroke-width: 2.5;
          stroke: currentColor;
        }
        @keyframes mobileCardSlideLeft {
          from {
            opacity: 0;
            transform: translateX(32px) scale(0.96);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
        @keyframes mobileCardSlideRight {
          from {
            opacity: 0;
            transform: translateX(-32px) scale(0.96);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
      `}</style>
      {/* Title Section */}
      <div className="px-10 pt-4 pb-10">
        <div className="mx-auto text-left">
          <h1
            className="text-2xl font-bold text-black mb-3 leading-tight"
            style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
          >
            Simplifying a day in the life of a consultant with WhiteOwl
          </h1>
          {/* <p
            className="text-lg font-normal text-black leading-relaxed"
            style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
          >
            Seamlessly connect the tools you use everyday.
          </p> */}
        </div>
      </div>

      {/* Individual carousels for Discover, Qualify, Pitch */}
      <div className="mx-auto px-4 space-y-8">
        {cardsData.map((card) => (
          <MobileCardCarousel
            key={card.id}
            card={card}
            videoData={videoData}
          />
        ))}
      </div>
    </div>
  )
}
